/**
 * BudgetApp - Main Application Entry Point
 * Production-ready modular structure
 */

// Import styles
import '../css/main.css';

// Import core modules
import { initializeAuth, setupAuthEventListeners, signOut, getCurrentUser, showPasswordResetModal, closePasswordResetModal, handlePasswordResetSubmit } from './modules/auth.js';
import { 
  initializeBudget, 
  calculateTotals, 
  saveCurrentBudget,
  saveCurrentVersion, 
  loadBudgetVersion, 
  renameCurrentVersion,
  initializeBudgetNameEditing,
  initializeInlineEditing,
  toggleVersionsPanel,
  setDefaultVersion,
  toggleDefaultVersion,
  duplicateVersion,
  deleteVersion,
  addIncomeItem,
  addExpenseCategory,
  addItemToCategory,
  removeItem,
  removeCategory,
  importBudgetFromFile,
  captureBudgetData, 
  restoreBudgetData,
  toggleRecurringPanel,
  saveRecurringConfig,
  cancelRecurringConfig,
  getRecurringExpenses,
  generateAllRecurringExpenses,
  getCurrentMonthRecurringExpenses,
  getUpcomingRecurringExpenses,
  testRecurringGeneration,
  initializeRecurringCalendar,
  navigateCalendar,
  renderCalendar,
  getCurrentBudgetData,
  initializeAutoLogging,
  processRecurringExpenses,
  manualProcessRecurringExpenses,
  getRecurringExpenseStatus,
  getCurrentBudgetId,
  getCurrentVersionName,
  getBudgetVersions,
  openTargetModal,
  closeTargetModal,
  saveTargetFromModal,
  updateModalInputRow,
  toggleIncomeFrequencyPanel,
  saveIncomeFrequencyConfig,
  cancelIncomeFrequencyConfig,
  toggleIncomeTaxPanel,
  saveIncomeTaxConfig,
  cancelIncomeTaxConfig,
  toggleIncomeTimingPanel,
  saveIncomeTimingConfig,
  cancelIncomeTimingConfig
} from './modules/budget.js';
import { initializeMobileSupport, toggleMenu, closeMenu } from './modules/utils/mobile.js';
import { setupCurrencyInput } from './modules/utils/currency.js';
import { initializeIncomeManagement } from './modules/incomeManagement.js';
import { initializeIncomeWizardUI, openIncomeWizard, closeIncomeWizard } from './modules/incomeWizardUI.js';
import { 
  setupLinkSharingEventListeners, 
  showLinkCodeModal, 
  showLinkRedemptionModal,
  updateLinkStatusIndicator,
  setLinkingState,
  validateCodeInputs,
  checkForFollowers,
  handleShareAccessClick,
  updateShareAccessButton,
  unlinkHousehold
} from './modules/linkSharing.js';
import { 
  initializeExpenseTracker,
  updateOverallProgress,
  deleteExpense,
  initializeExpenseTrackerHistory,
  editExpenseFromTrackerHistory,
  deleteExpenseFromTrackerHistory,
  saveExpenseEdit,
  cancelExpenseEdit,
  clearExpenseTrackerHistoryFilter,
  filterExpenseTrackerHistoryByCategory
} from './modules/expenseTracker.js';
import { 
  initializeVisualizations,
  renderSankeyDiagram,
  resizeChart,
  renderBudgetVsActualChart,
  initializeHistoricalTrends,
  switchBarChartMode,
  updateFlowSummaryCards
} from './modules/visualizations.js';
import { initializeSettings, switchToSettings, getDefaultView } from './modules/settings.js';
import { updateComparison, refreshComparison } from './modules/comparison.js';
import achievementEngine from './modules/achievements.js';
import achievementUI, { initializeAchievementsView } from './modules/achievementUI.js';

/**
 * Application State
 */
let isCompareMode = false;
let isSankeyMode = false;
let isProgressViewMode = false;
let isRecurringCalendarMode = false;
let isExpenseTrackerMode = false;
let isSettingsMode = false;
let isAchievementsMode = false;

/**
 * Hide iOS Safari address bar on page load
 */
function hideIOSAddressBar() {
  // Check if we're on iOS Safari
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  
  if (isIOS && isSafari) {
    // Use setTimeout to ensure the page is fully loaded
    setTimeout(() => {
      // Scroll down slightly to hide the address bar
      window.scrollTo(0, 1);
      
      // Then scroll back to top
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }, 100);
    
    // Also trigger on orientation change
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        window.scrollTo(0, 1);
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      }, 500);
    });
  }
}

/**
 * Ensure styles are loaded before showing content
 */
function ensureStylesLoaded() {
  return new Promise((resolve) => {
    // Check if styles are already loaded
    if (document.readyState === 'complete') {
      document.documentElement.classList.add('styles-loaded');
      resolve();
      return;
    }
    
    // Wait for page load
    const onLoad = () => {
      document.documentElement.classList.add('styles-loaded');
      window.removeEventListener('load', onLoad);
      resolve();
    };
    
    window.addEventListener('load', onLoad);
    
    // Fallback timeout
    setTimeout(() => {
      document.documentElement.classList.add('styles-loaded');
      resolve();
    }, 1000);
  });
}

/**
 * Initialize the application
 */
async function initializeApp() {
  
  try {
    // Ensure styles are loaded to prevent FOUC
    await ensureStylesLoaded();
    
    // Hide iOS Safari address bar
    hideIOSAddressBar();
    
    // Initialize mobile support
    initializeMobileSupport();
    
    // Setup authentication event listeners
    setupAuthEventListeners();
    
    // Setup link sharing event listeners
    setupLinkSharingEventListeners();
    
    // Initialize authentication state observer
    initializeAuth(async (user) => {
      if (user) {
        
        // Clear expense cache on user authentication to ensure fresh data
        if (typeof window !== 'undefined' && window.clearExpenseCache) {
          window.clearExpenseCache();
        }
        
        await initializeBudget(user);
        
        await initializeIncomeManagement();
        
        await initializeSettings();
        
        // Set default view after settings are loaded, with a small delay to ensure settings are populated
        setTimeout(() => setDefaultViewOnStartup(), 100);
        
        setupBudgetEventListeners();
        setupViewSwitchingEventListeners();
        setupMenuEventListeners();
        initializeIncomeWizardUI();
        
        // Initialize auto-logging for recurring expenses
        initializeAutoLogging();
        
        // Initialize achievement system
        await achievementEngine.initialize(user);
        await achievementUI.initialize();
        
        // Ensure floating button is properly initialized
        setTimeout(() => updateFloatingButton(), 100);
        
      }
    });
    
    // Setup global event listeners
    setupGlobalEventListeners();
    
    console.log('BudgetApp initialized successfully');
    
  } catch (error) {
    console.error('Failed to initialize app:', error);
  }
}

/**
 * Setup budget-related event listeners
 */
function setupBudgetEventListeners() {
  // Setup currency inputs
  const incomeInputs = document.querySelectorAll('.income-input');
  const expenseInputs = document.querySelectorAll('.expense-input');
  
  [...incomeInputs, ...expenseInputs].forEach(input => {
    setupCurrencyInput(input, calculateTotals);
  });
  
  // Version management buttons
  const saveBtn = document.querySelector('[onclick="saveCurrentVersion()"]');
  if (saveBtn) {
    saveBtn.onclick = saveCurrentVersion;
  }
}

/**
 * Setup view switching event listeners
 */
function setupViewSwitchingEventListeners() {
  // Budget view
  const budgetViewBtn = document.getElementById('single-view-btn');
  if (budgetViewBtn) {
    budgetViewBtn.addEventListener('click', switchToSingleView);
  }
  
  // Compare view
  const compareViewBtn = document.getElementById('compare-view-btn');
  if (compareViewBtn) {
    compareViewBtn.addEventListener('click', switchToCompareView);
  }
  
  // Sankey view
  const sankeyViewBtn = document.getElementById('sankey-view-btn');
  if (sankeyViewBtn) {
    sankeyViewBtn.addEventListener('click', switchToSankeyView);
  }
  
  // Progress view
  const progressViewBtn = document.getElementById('progress-view-btn');
  if (progressViewBtn) {
    progressViewBtn.addEventListener('click', switchToProgressView);
  }
  
  // Recurring calendar
  const recurringCalendarBtn = document.getElementById('recurring-calendar-btn');
  if (recurringCalendarBtn) {
    recurringCalendarBtn.addEventListener('click', switchToRecurringCalendar);
  }
  
  // Expense tracker
  const expenseTrackerBtn = document.getElementById('expense-tracker-btn');
  if (expenseTrackerBtn) {
    expenseTrackerBtn.addEventListener('click', switchToExpenseTracker);
  }
  
  // Achievements
  const achievementsBtn = document.getElementById('achievements-btn');
  if (achievementsBtn) {
    achievementsBtn.addEventListener('click', switchToAchievements);
  }
  
  // Settings
  const settingsBtn = document.getElementById('settings-btn');
  if (settingsBtn) {
    settingsBtn.addEventListener('click', switchToSettingsView);
  }
}

/**
 * Setup menu event listeners
 */
function setupMenuEventListeners() {
  // Menu toggle - using onclick in HTML
  // Menu overlay - using onclick in HTML
  
  // Note: Other menu buttons use onclick handlers in HTML for simplicity
  // This ensures they work even if JavaScript modules load after DOM
}

/**
 * Setup global event listeners
 */
function setupGlobalEventListeners() {
  // Close modals on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Close any open modals
      const modals = document.querySelectorAll('.modal, .link-code-modal, .link-redemption-modal');
      modals.forEach(modal => {
        if (modal.classList.contains('active')) {
          modal.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
      
      // Close target modals
      const targetModals = document.querySelectorAll('.target-modal.active');
      targetModals.forEach(modal => modal.classList.remove('active'));
    }
  });
  
  // Close target modals when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.target-modal') && !e.target.closest('.target-icon')) {
      const openModals = document.querySelectorAll('.target-modal.active');
      openModals.forEach(modal => modal.classList.remove('active'));
    }
  });
  
  // Setup logo click handlers for navigation to home
  const mainLogo = document.querySelector('.header-logo');
  const stickyLogo = document.querySelector('.sticky-logo');
  
  if (mainLogo) {
    mainLogo.addEventListener('click', () => {
      console.log('Main logo clicked');
      navigateToHome();
    });
    mainLogo.style.cursor = 'pointer';
  }
  
  if (stickyLogo) {
    stickyLogo.addEventListener('click', () => {
      console.log('Sticky logo clicked');
      navigateToHome();
    });
    stickyLogo.style.cursor = 'pointer';
  }
  
  // Setup sticky header scroll behavior
  setupStickyHeader();
}

/**
 * Setup sticky header functionality
 */
function setupStickyHeader() {
  const stickyHeader = document.getElementById('sticky-header');
  const mainHeader = document.querySelector('.header');
  
  if (!stickyHeader || !mainHeader) return;
  
  let lastScrollY = window.scrollY;
  let ticking = false;
  
  function updateStickyHeader() {
    const scrollY = window.scrollY;
    const headerHeight = mainHeader.offsetHeight;
    
    // Show sticky header when scrolled past main header
    if (scrollY > headerHeight && scrollY > lastScrollY) {
      // Scrolling down past header
      stickyHeader.classList.add('visible');
    } else if (scrollY <= headerHeight) {
      // Back to top area
      stickyHeader.classList.remove('visible');
    }
    
    lastScrollY = scrollY;
    ticking = false;
    
    // Sync the status indicators
    syncStatusIndicators();
  }
  
  function requestTick() {
    if (!ticking) {
      requestAnimationFrame(updateStickyHeader);
      ticking = true;
    }
  }
  
  // Listen for scroll events
  window.addEventListener('scroll', requestTick, { passive: true });
  
  // Setup sticky menu toggle click handler (in case onclick doesn't work)
  const stickyMenuToggle = document.getElementById('sticky-menu-toggle');
  if (stickyMenuToggle) {
    stickyMenuToggle.addEventListener('click', (e) => {
      console.log('Sticky menu toggle clicked');
      console.log('Menu elements check:');
      console.log('- headerControls:', document.getElementById('header-controls'));
      console.log('- overlay:', document.getElementById('menu-overlay'));
      console.log('- toggleMenu function:', typeof toggleMenu);
      e.preventDefault();
      e.stopPropagation();
      toggleMenu();
    });
  } else {
    console.log('Sticky menu toggle not found during setup');
  }
  
  // Initial sync
  syncStatusIndicators();
}

/**
 * Sync status indicators between main and sticky headers
 */
function syncStatusIndicators() {
  const mainIndicator = document.getElementById('sync-indicator');
  const stickyIndicator = document.getElementById('sticky-sync-indicator');
  
  if (mainIndicator && stickyIndicator) {
    // Copy all classes from main to sticky
    stickyIndicator.className = mainIndicator.className.replace('sync-indicator', 'sticky-sync-indicator');
  }
  
  // Sync menu toggle states
  const mainMenuToggle = document.getElementById('menu-toggle');
  const stickyMenuToggle = document.getElementById('sticky-menu-toggle');
  
  if (mainMenuToggle && stickyMenuToggle) {
    if (mainMenuToggle.classList.contains('active')) {
      stickyMenuToggle.classList.add('active');
    } else {
      stickyMenuToggle.classList.remove('active');
    }
  }
}

/**
 * View switching functions
 */
function switchToSingleView() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('single-mode');
  setActiveViewButton('single-view-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  updateFloatingButton();
}

function switchToCompareView() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('compare-mode');
  setActiveViewButton('compare-view-btn');
  isCompareMode = true;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  updateFloatingButton();
  
  // Initialize compare view
  updateComparisonView();
}

function switchToSankeyView() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('sankey-mode');
  setActiveViewButton('sankey-view-btn');
  isCompareMode = false;
  isSankeyMode = true;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  updateFloatingButton();
  
  // Initialize Sankey view
  renderSankeyChart();
  
  // Setup balance input after a short delay to ensure DOM is ready
  setTimeout(() => {
    if (typeof setupBalanceInput === 'function') {
      setupBalanceInput();
    }
  }, 100);
}

function switchToProgressView() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('progress-view-mode');
  setActiveViewButton('progress-view-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = true;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  updateFloatingButton();
  
  // Initialize progress view
  updateProgressView();
}

async function switchToRecurringCalendar() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('recurring-calendar-mode');
  setActiveViewButton('recurring-calendar-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = true;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  updateFloatingButton();
  
  // Initialize recurring calendar
  await initializeRecurringCalendar();
}

function switchToExpenseTracker() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('expense-tracker-mode');
  setActiveViewButton('expense-tracker-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = true;
  isAchievementsMode = false;
  updateFloatingButton();
  
  // Initialize expense tracker
  updateExpenseTracker();
}

function switchToAchievements() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  hideAllViews();
  showView('achievements-mode');
  setActiveViewButton('achievements-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isSettingsMode = false;
  isAchievementsMode = true;
  updateFloatingButton();
  
  // Initialize achievements view
  initializeAchievementsView();
}

function switchToSettingsView() {
  closeMenu(); // Close menu when view is selected
  window.scrollTo(0, 0); // Reset scroll position
  switchToSettings(); // Call the settings module function
  setActiveViewButton('settings-btn');
  isCompareMode = false;
  isSankeyMode = false;
  isProgressViewMode = false;
  isRecurringCalendarMode = false;
  isExpenseTrackerMode = false;
  isAchievementsMode = false;
  isSettingsMode = true;
  updateFloatingButton();
}

/**
 * Set default view on app startup based on user preferences
 */
function setDefaultViewOnStartup() {
  try {
    // Check if settings are loaded first
    import('./modules/settings.js').then(async settingsModule => {
      const currentSettings = settingsModule.getCurrentSettings();
      
      if (!currentSettings) {
        setTimeout(() => setDefaultViewOnStartup(), 200);
        return;
      }
      
      // Now get the default view with properly loaded settings
      const defaultView = settingsModule.getDefaultView();
      
      // Check if budget data is loaded
      const currentBudget = getCurrentBudgetData();
      const budgetVersions = getBudgetVersions();
      const hasBudgetData = currentBudget && currentBudget.categories && Object.keys(currentBudget.categories).length > 0;
      const isNewUser = !budgetVersions || Object.keys(budgetVersions).length === 0;
      
      // If budget data hasn't loaded yet (functions return null), wait and retry
      if (currentBudget === null || budgetVersions === null) {
        setTimeout(() => setDefaultViewOnStartup(), 200);
        return;
      }
      
      // For completely new users (no budget data AND no budget versions), show welcome screen
      if (isNewUser && !hasBudgetData) {
        switchToSingleView();
        return;
      }
      
      // If we don't have budget data but want to go to expenses, wait and retry
      if (!hasBudgetData && defaultView === 'expenses') {
        setTimeout(() => {
          const retryBudget = getCurrentBudgetData();
          const retryHasBudgetData = retryBudget && retryBudget.categories && Object.keys(retryBudget.categories).length > 0;
          
          if (retryHasBudgetData) {
            switchToExpenseTracker();
          } else {
            switchToSingleView();
          }
        }, 300);
        return;
      }
      
      // For non-expense views or when we have budget data, switch immediately
      switchToView(defaultView);
    });
    
  } catch (error) {
    console.error('Error setting default view:', error);
    switchToSingleView(); // Fallback to budget view
  }
}

function switchToView(viewName) {
  switch (viewName) {
    case 'budget':
      switchToSingleView();
      break;
    case 'progress':
      switchToProgressView();
      break;
    case 'expenses':
      switchToExpenseTracker();
      break;
    default:
      switchToSingleView(); // Fallback to budget view
  }
}

/**
 * Navigate to home/default page (for logo clicks)
 */
function navigateToHome() {
  console.log('Navigating to home/default page');
  
  // Close menu immediately to prevent visual confusion
  closeMenu();
  
  // Check if user is completely new (no budget versions AND no data)
  const budgetVersions = getBudgetVersions();
  const currentBudget = getCurrentBudgetData();
  const hasBudgetData = currentBudget && currentBudget.categories && Object.keys(currentBudget.categories).length > 0;
  const isTrulyNewUser = (!budgetVersions || Object.keys(budgetVersions).length === 0) && !hasBudgetData;
  
  // For truly new users, always show budget view to display welcome screen
  if (isTrulyNewUser) {
    console.log('Truly new user detected in navigateToHome, showing budget view with welcome screen');
    switchToSingleView();
    return;
  }
  
  const defaultView = getDefaultView();
  
  switch (defaultView) {
    case 'budget':
      switchToSingleView();
      break;
    case 'progress':
      switchToProgressView();
      break;
    case 'expenses':
      // Check if we have budget data for expense tracker
      if (hasBudgetData) {
        switchToExpenseTracker();
      } else {
        switchToSingleView(); // Fallback if no budget data
      }
      break;
    default:
      switchToSingleView(); // Fallback to budget view
  }
}

/**
 * Helper functions for view management
 */
function hideAllViews() {
  const views = [
    'single-mode',
    'compare-mode', 
    'sankey-mode',
    'progress-view-mode',
    'recurring-calendar-mode',
    'expense-tracker-mode',
    'achievements-mode',
    'settings-mode'
  ];
  
  views.forEach(viewId => {
    const viewEl = document.getElementById(viewId);
    if (viewEl) {
      viewEl.style.display = 'none';
    }
  });
}

function showView(viewId) {
  const viewEl = document.getElementById(viewId);
  if (viewEl) {
    if (viewId === 'expense-tracker-mode') {
      viewEl.style.display = 'flex';
      viewEl.style.flexDirection = 'column';
    } else {
      viewEl.style.display = 'block';
    }
  }
}

function setActiveViewButton(buttonId) {
  // Remove active class from all view buttons
  const viewButtons = document.querySelectorAll('.header-btn');
  viewButtons.forEach(btn => btn.classList.remove('active'));
  
  // Add active class to current button
  const activeBtn = document.getElementById(buttonId);
  if (activeBtn) {
    activeBtn.classList.add('active');
  }
}

/**
 * Create and manage floating add expense button
 * Show on mobile for all views except expense tracker
 */
function createFloatingButton() {
  // Check if button already exists
  if (document.getElementById('floating-add-expense-btn')) {
    return;
  }
  
  // Create the floating button element
  const floatingBtn = document.createElement('button');
  floatingBtn.id = 'floating-add-expense-btn';
  floatingBtn.className = 'floating-add-btn';
  floatingBtn.onclick = () => switchToExpenseTracker();
  
  // Create inner elements
  const icon = document.createElement('span');
  icon.className = 'floating-btn-icon';
  icon.textContent = '+';
  
  const text = document.createElement('span');
  text.className = 'floating-btn-text';
  text.textContent = 'Add Expense';
  
  floatingBtn.appendChild(icon);
  floatingBtn.appendChild(text);
  
  // Insert before the main container to ensure proper stacking context
  const mainContainer = document.getElementById('main-container');
  if (mainContainer) {
    document.body.insertBefore(floatingBtn, mainContainer);
    console.log('Floating button inserted before main container');
  } else {
    // Fallback: append to body
    document.body.appendChild(floatingBtn);
    console.log('Floating button appended to body (main container not found)');
  }
}

function updateFloatingButton() {
  // Ensure button exists
  createFloatingButton();
  
  const floatingBtn = document.getElementById('floating-add-expense-btn');
  if (floatingBtn) {
    // Hide button if currently on expense tracker view
    if (isExpenseTrackerMode) {
      document.body.classList.add('expense-tracker-active');
    } else {
      document.body.classList.remove('expense-tracker-active');
    }
  }
}

/**
 * Placeholder functions for views that need implementation
 * These would be moved to separate view modules in a complete refactor
 */
async function updateComparisonView() {
  console.log('Updating comparison view...');
  try {
    await updateComparison();
  } catch (error) {
    console.error('Failed to update comparison view:', error);
  }
}

async function renderSankeyChart() {
  console.log('Rendering Sankey chart...');
  try {
    await initializeVisualizations();
    await renderSankeyDiagram();
  } catch (error) {
    console.error('Failed to render Sankey chart:', error);
  }
}

async function updateProgressView() {
  try {
    // Clear any existing chart content first
    const container = document.getElementById('budget-vs-actual-container');
    if (container) {
      container.innerHTML = '<div style="padding: 20px; text-align: center;">Loading progress data...</div>';
    }
    
    // Add a small delay to ensure the expense data is available
    // This helps when switching immediately after adding expenses
    await new Promise(resolve => setTimeout(resolve, 100));
    
    await renderBudgetVsActualChart('budget-vs-actual-container');
    
    // Initialize historical trends chart
    initializeHistoricalTrends();
  } catch (error) {
    console.error('Failed to render budget vs actual chart:', error);
  }
}

// Global function to refresh progress data across all modules
async function refreshAllProgressData() {
  console.log('Refreshing all progress data across modules...');
  try {
    // Refresh progress view if it's currently active
    if (isProgressViewMode) {
      await updateProgressView();
    }
    
    // Could add other module refreshes here in the future
    // if (isExpenseTrackerMode) { await updateExpenseTracker(); }
    
  } catch (error) {
    console.error('Failed to refresh progress data:', error);
  }
}

async function updateExpenseTracker() {
  console.log('Initializing expense tracker view...');
  try {
    await initializeExpenseTracker();
    
    // Initialize expense history section
    initializeExpenseTrackerHistory();
  } catch (error) {
    console.error('Failed to initialize expense tracker:', error);
  }
}

/**
 * Export functions for global access (for HTML onclick handlers)
 */
if (typeof window !== 'undefined') {
  // View switching
  window.switchToSingleView = switchToSingleView;
  window.switchToCompareView = switchToCompareView;
  window.switchToSankeyView = switchToSankeyView;
  window.switchToProgressView = switchToProgressView;
  window.switchToRecurringCalendar = switchToRecurringCalendar;
  window.switchToExpenseTracker = switchToExpenseTracker;
  window.switchToAchievements = switchToAchievements;
  window.switchToSettings = switchToSettingsView;
  window.navigateToHome = navigateToHome;
  
  // Progress data refresh function
  window.refreshAllProgressData = refreshAllProgressData;
  
  // Menu functions
  window.toggleMenu = toggleMenu;
  window.closeMenu = closeMenu;
  
  // Auth functions
  window.signOut = signOut;
  window.showPasswordResetModal = showPasswordResetModal;
  window.closePasswordResetModal = closePasswordResetModal;
  window.sendPasswordReset = handlePasswordResetSubmit;
  
  // Budget functions
  window.saveCurrentBudget = saveCurrentBudget;
  window.saveCurrentVersion = saveCurrentVersion;
  window.loadBudgetVersion = loadBudgetVersion;
  window.calculateTotals = calculateTotals;
  window.renameCurrentVersion = renameCurrentVersion;
  window.initializeBudgetNameEditing = initializeBudgetNameEditing;
  window.initializeInlineEditing = initializeInlineEditing;
  window.toggleVersionsPanel = toggleVersionsPanel;
  window.setDefaultVersion = setDefaultVersion;
  window.toggleDefaultVersion = toggleDefaultVersion;
  window.duplicateVersion = duplicateVersion;
  window.deleteVersion = deleteVersion;
  window.addIncomeItem = addIncomeItem;
  window.addExpenseCategory = addExpenseCategory;
  window.addItemToCategory = addItemToCategory;
  window.removeItem = removeItem;
  window.removeCategory = removeCategory;
  window.importBudgetFromFile = importBudgetFromFile;
  
  // Link sharing functions
  window.showLinkCodeModal = showLinkCodeModal;
  window.showLinkRedemptionModal = showLinkRedemptionModal;
  window.validateCodeInputs = validateCodeInputs;
  window.checkForFollowers = checkForFollowers;
  window.handleShareAccessClick = handleShareAccessClick;
  window.updateShareAccessButton = updateShareAccessButton;
  window.unlinkHousehold = unlinkHousehold;
  
  // Recurring expense functions
  window.toggleRecurringPanel = toggleRecurringPanel;
  window.saveRecurringConfig = saveRecurringConfig;
  window.cancelRecurringConfig = cancelRecurringConfig;
  window.getRecurringExpenses = getRecurringExpenses;
  window.generateAllRecurringExpenses = generateAllRecurringExpenses;
  window.getCurrentMonthRecurringExpenses = getCurrentMonthRecurringExpenses;
  window.getUpcomingRecurringExpenses = getUpcomingRecurringExpenses;
  window.testRecurringGeneration = testRecurringGeneration;
  window.navigateCalendar = navigateCalendar;
  window.renderCalendar = renderCalendar;
  
  // Auto-logging functions
  window.processRecurringExpenses = processRecurringExpenses;
  window.manualProcessRecurringExpenses = manualProcessRecurringExpenses;
  window.getRecurringExpenseStatus = getRecurringExpenseStatus;
  
  // Income frequency functions
  window.toggleIncomeFrequencyPanel = toggleIncomeFrequencyPanel;
  window.saveIncomeFrequencyConfig = saveIncomeFrequencyConfig;
  window.cancelIncomeFrequencyConfig = cancelIncomeFrequencyConfig;
  
  // Income tax functions
  window.toggleIncomeTaxPanel = toggleIncomeTaxPanel;
  window.saveIncomeTaxConfig = saveIncomeTaxConfig;
  window.cancelIncomeTaxConfig = cancelIncomeTaxConfig;
  
  // Income timing functions
  window.toggleIncomeTimingPanel = toggleIncomeTimingPanel;
  window.saveIncomeTimingConfig = saveIncomeTimingConfig;
  window.cancelIncomeTimingConfig = cancelIncomeTimingConfig;
  
  // Expense tracker functions
  window.initializeExpenseTracker = initializeExpenseTracker;
  window.updateOverallProgress = updateOverallProgress;
  window.deleteExpense = deleteExpense;
  
  // Expense tracker history functions
  window.editExpenseFromTrackerHistory = editExpenseFromTrackerHistory;
  window.deleteExpenseFromTrackerHistory = deleteExpenseFromTrackerHistory;
  window.saveExpenseEdit = saveExpenseEdit;
  window.cancelExpenseEdit = cancelExpenseEdit;
  window.clearExpenseTrackerHistoryFilter = clearExpenseTrackerHistoryFilter;
  window.filterExpenseTrackerHistoryByCategory = filterExpenseTrackerHistoryByCategory;
  
  // Visualization functions
  window.initializeVisualizations = initializeVisualizations;
  window.renderSankeyDiagram = renderSankeyDiagram;
  window.resizeChart = resizeChart;
  window.renderBudgetVsActualChart = renderBudgetVsActualChart;
  window.initializeHistoricalTrends = initializeHistoricalTrends;
  window.switchBarChartMode = switchBarChartMode;
  window.updateFlowSummaryCards = updateFlowSummaryCards;
  
  
  // Budget helper functions
  window.getCurrentBudgetData = getCurrentBudgetData;
  window.getCurrentBudgetId = getCurrentBudgetId;
  window.getCurrentVersionName = getCurrentVersionName;
  window.getBudgetVersions = getBudgetVersions;
  
  // Category target functions
  window.openTargetModal = openTargetModal;
  window.closeTargetModal = closeTargetModal;
  window.saveTargetFromModal = saveTargetFromModal;
  window.updateModalInputRow = updateModalInputRow;
  
  // Comparison functions
  window.updateComparison = updateComparison;
  window.refreshComparison = refreshComparison;

  // Income management functions
  window.openIncomeWizard = openIncomeWizard;
  window.closeIncomeWizard = closeIncomeWizard;
  
  // Floating button management
  window.createFloatingButton = createFloatingButton;
  window.updateFloatingButton = updateFloatingButton;
}

/**
 * Initialize the app when DOM is ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeApp();
  });
} else {
  initializeApp();
}

export { initializeApp };